export const MG_ASSET_CDN_URL = "https://assets.meaningfulgigs.com";
const MG_ASSET_ALT_URL = "https://static.puntt.ai";

export function assetForUser(path?: string, size = 100, hsize = 0) {
  if (!path) return undefined;
  // Absolute local path served from the SPA's public/ folder. Used by demo
  // seed assets like /seed/vitalora/avatars/james.jpg. Skip CDN rewriting.
  if (path.startsWith("/")) {
    return path;
  }
  // Legacy avatar path
  if (path.includes("100x100")) {
    return `${MG_ASSET_CDN_URL}/fit-in/${size}x${hsize || size}/${path}`;
  }
  // New avatar path (uploaded, not resized)
  if (path.includes("puntt/misc/edit-profile/users/")) {
    return `${MG_ASSET_ALT_URL}/${path}`;
  }
  // fallback to original logic
  return resolvePath(path, size, hsize);
}

export function getEnterpriseLogo(path?: string) {
  if (!path) {
    return `${MG_ASSET_CDN_URL}/misc/company.png`;
  }

  if (path.includes("http")) {
    return path;
  }

  return `${MG_ASSET_CDN_URL}/${path}`;
}

export function resolvePath(path?: string | null, size = 100, hsize = 0) {
  if (!path) {
    return undefined;
  }

  if (path.startsWith("/")) {
    return path;
  }

  if (path.includes("http")) {
    return path;
  }

  if (path.includes("users")) {
    return `${MG_ASSET_ALT_URL}/${path}`;
  }

  return `${MG_ASSET_CDN_URL}/fit-in/${size}x${hsize || size}/${path}`;
}

export function resizeImage(file: File): Promise<File> {
  return new Promise((resolve) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.src = e.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        const max_width = 1200;
        const max_height = 1200;

        let width = img.width;
        let height = img.height;
        if (width > max_width || height > max_height) {
          if (width > height) {
            if (width > max_width) {
              height *= max_width / width;
              width = max_width;
            }
          } else {
            if (height > max_height) {
              width *= max_height / height;
              height = max_height;
            }
          }
        }

        canvas.width = width;
        canvas.height = height;

        ctx?.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(new File([blob], file.name, { type: file.type }));
            } else {
              resolve(file);
            }
          },
          "image/jpeg",
          0.9,
        );
      };
    };

    reader.readAsDataURL(file);
  });
}

export function resizeImageToSquare(file: File, size: number): Promise<File> {
  return new Promise((resolve) => {
    // For GIFs, check if they're animated
    if (file.type === "image/gif") {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const arrayBuffer = e.target?.result as ArrayBuffer;
        const byteArray = new Uint8Array(arrayBuffer);
        let isAnimated = false;
        let i = 0;

        // GIF frame signature
        const FRAME_SIGNATURE = [0x21, 0xf9, 0x04];

        while (i < byteArray.length - 3) {
          if (
            byteArray[i] === FRAME_SIGNATURE[0] &&
            byteArray[i + 1] === FRAME_SIGNATURE[1] &&
            byteArray[i + 2] === FRAME_SIGNATURE[2]
          ) {
            isAnimated = true;
            break;
          }
          i++;
        }

        // If it's an animated GIF, return it as is
        if (isAnimated) {
          resolve(file);
          return;
        }
      };
      reader.readAsArrayBuffer(file);
    }

    // For non-animated GIFs and other image types, proceed with resizing
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.src = e.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = size;
        canvas.height = size;
        // Center-crop to square
        let sx = 0,
          sy = 0,
          sw = img.width,
          sh = img.height;
        if (img.width > img.height) {
          sx = (img.width - img.height) / 2;
          sw = img.height;
        } else if (img.height > img.width) {
          sy = (img.height - img.width) / 2;
          sh = img.width;
        }
        ctx?.drawImage(img, sx, sy, sw, sh, 0, 0, size, size);
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(new File([blob], file.name, { type: file.type }));
            } else {
              resolve(file);
            }
          },
          file.type === "image/gif" ? "image/gif" : "image/jpeg",
          0.9,
        );
      };
    };
    reader.readAsDataURL(file);
  });
}
